import { differenceInCalendarDays, isToday } from "date-fns";
import { isEmpty } from "lodash";
import React, { useMemo } from "react";
import Tooltip from "@components/Tooltip";
import theme from "@styles/theme";
import {
  CleaningChoice,
  Room,
  RoomSpace,
  SpaceService,
} from "@store/space/types";
import { makeSelectRoomSpaceById } from "@store/space/spaceSelectors";
import useAppSelector from "@hooks/useAppSelector";
import { useTranslation } from "react-i18next";

interface RoomStatusProps {
  spaceId: RoomSpace["id"];
  showArrivals?: boolean;
  showGuestsAmount?: boolean;
}

const RoomStatus: React.FC<RoomStatusProps> = ({ spaceId, showArrivals, showGuestsAmount }) => {
  const { t } = useTranslation();
  const selectSpaceById = useMemo(makeSelectRoomSpaceById, [spaceId]);
  const space = useAppSelector((state) => selectSpaceById(state, spaceId));

  const {
    guest: { current, next },
  } = space.room;
  let services: SpaceService[] = [];
  if (!!current && !showArrivals && !showGuestsAmount) services = current.services;
  if (!!next && showArrivals) services = next.services;

  const amountOfServices = services.length;
  const firstExtra = services[0];

  const getGuestsAmount = () => {
    if (!current) return "";
    if (current.children) return `${current.adults} + ${current.children} 👶`;

    return current.adults;
  };

  if (showGuestsAmount) return <span className="rs guests-amount">{getGuestsAmount()}</span>;

  return (
    <>
      <div className="status">
        <Status
          room={space.room}
          duration={current?.duration}
          showArrivals={showArrivals}
        />
        {amountOfServices === 1 && (
          <span className="extra-service extra-service-yellow">
            {firstExtra.label}
          </span>
        )}
        {amountOfServices > 1 && (
          <div className="extra-services">
            <span className="extra-service extra-service-yellow">
              {t("hk.amountExtra", { amount: amountOfServices })}
            </span>
            <div className="hover">
              <Tooltip color="#E2F5FE">
                <h4
                  style={{
                    marginLeft: "10px",
                    fontWeight: 600,
                    color: theme.colors.brandBlue,
                    minWidth: "125px",
                  }}
                >
                  {t("extra.self")}
                </h4>
                {services.map((s) => (
                  <span className="extra-service extra-service-blue">
                    - {s.label}
                  </span>
                ))}
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      <style global jsx>{`
        .status {
          display: flex;
          flex-wrap: wrap;
        }
        .extra-service {
          display: inline-block;
          font-weight: normal;
          background-color: ${theme.colors.gray};
          box-sizing: border-box;
          white-space: normal;
          border-radius: 4px;
          font-size: 14px;
          padding: 4px 8px;
          margin-left: 4px;
        }
        .extra-service-yellow {
          color: ${theme.colors.brandGoldDark};
          background-color: ${theme.colors.yellow};
        }
        .extra-service-blue {
          color: ${theme.colors.brandBlue};
          background-color: ${theme.colors.primary300};
          padding-left: ${8}px;
        }
        .hover {
          position: relative;
          display: none;
        }
        .extra-services {
          cursor: pointer;
        }
        .extra-services:hover > .hover {
          display: block;
        }

        .rs {
          display: inline-block;
          font-weight: normal;
          background-color: ${theme.colors.gray};
          box-sizing: border-box;
          white-space: normal;
          border-radius: 4px;
          font-size: 14px;
          padding: 4px 8px;
        }
        .rs:not(:first-child) {
          margin-left: 4px;
        }

        .guests-amount, .guests-amount:nth-child(odd) {
          padding: 4px 12px;
          background-color: transparent;
          font-size: 16px;
          font-weight: 600;
        }

        /* Tablet optimizations */
        @media only screen and (min-device-width: 481px) and (max-device-width: 1024px) {
          .rs {
            font-size: 12px;
          }
        }
      `}</style>
    </>
  );
};

export default RoomStatus;

interface StatusProps {
  room: Room;
  duration?: { total: number; day: number };
  showArrivals?: boolean;
}

const Status: React.FC<StatusProps> = ({ room, duration, showArrivals }) => {
  const { t } = useTranslation();
  const { vacant, staying, cleaningChoice, doNotDisturb } = room;

  const daysVacant = (date?: string): string => {
    if (!date) return "Unknown date";
    const vacantDays = differenceInCalendarDays(new Date(), new Date(date));
    return vacantDays > 0
      ? t("hk.roomState.extra.vacant", { count: vacantDays })
      : t("hk.roomState.extra.vacantToday");
  };

  if (showArrivals && !isEmpty(room.guest?.next))
    return <span className="rs">aankomst</span>;
  if (showArrivals) return null;

  if (vacant) return <span className="rs">{daysVacant(room.vacantSince)}</span>;

  if (!!room.guest.current && isToday(new Date(room.guest.current.arrival)))
    return <span className="rs">{t("hk.roomState.extra.occupiedToday")}</span>;

  if (!staying)
    return <span className="rs">{t("hk.roomState.extra.leaving")}</span>;

  const isSkipCleaning = [
    CleaningChoice.NON_GREEN_SKIP,
    CleaningChoice.GREEN,
  ].includes(cleaningChoice);
  return (
    <>
      {staying && duration?.total && (
        <span className="rs">
          {t("hk.roomState.extra.staying", {
            current: duration.day,
            total: duration.total,
          })}
        </span>
      )}
      {cleaningChoice === CleaningChoice.ORANGE && (
        <span className="extra-service extra-service-yellow">
          {t("hk.roomState.extra.orange")}
        </span>
      )}
      {isSkipCleaning && (
        <span className="rs">{t("hk.roomState.extra.skipCleaning")}</span>
      )}
      {doNotDisturb && (
        <span className="extra-service extra-service-yellow">
          {t("hk.roomState.extra.doNotDisturb")}
        </span>
      )}
    </>
  );
};
